/**
 * The numbers a cooperative sitting decides "whose body is this" with (CAM-07, D-076(1), D-077(3)).
 *
 * Same discipline as `camera-readiness.ts` and `tunables.ts` (D-010/D-023): **every value here is
 * invented and flagged**. No source document names a separation, a displacement or a frame count —
 * MIN §6 and GDS §7 say only that lanes are fixed, that children do not swap, and that identity
 * comes from lane, calibration, body position and session player id. What these numbers do is decide
 * when the platform is *allowed to be sure*; they score nothing, they gate nothing academic, and
 * every one of them fails safe towards "we could not tell" rather than towards a guess.
 *
 * They are seeded from the Slice 0′ spike's executed measurements (D-077(2)) and are due for tuning
 * at the batched physical session with two children in one room — the spike's fixture was one adult
 * photograph composited at two scales, so nothing below has met a child.
 *
 * Distances are fractions of frame width in **mirrored** space: the side of the screen a child
 * watches themselves occupy (CAM-04), which is the same space `laneBounds` is expressed in, so
 * there is one convention and not two.
 */
export const COOPERATIVE_LANE_THRESHOLDS = {
  /**
   * How far apart two bodies' centres must be before the resolver will claim they are two children
   * in two lanes.
   *
   * Below it the two readings are treated as one indistinguishable situation and **neither** lane is
   * attributed. Deliberately smaller than the readiness screen's `minCentreSeparation` (0.22, D-078):
   * that number is about whether a room is comfortable enough to recommend, and this one is about
   * whether an attribution would be honest. A crowded room should play awkwardly, not silently put
   * one child's answers on the other child's profile.
   */
  minLaneSeparation: 0.12,

  /**
   * How much further apart they must get before ambiguity lifts.
   *
   * Without it, two children hovering at the threshold would flicker between attributed and
   * ambiguous every frame, and each flicker costs a retraction and a neutral re-baseline — a child
   * would watch their held answer evaporate repeatedly for standing still.
   */
  laneSeparationHysteresis: 0.04,

  /**
   * Below this, two readings cannot be two bodies at all: they are one child read twice.
   *
   * D-077(2)(b) measured exactly this — a body half out of frame produced readings 0.010 apart
   * (x 0.737 and 0.727) — and named a naive "one reading per lane" the single most dangerous
   * mechanism the spike found. Distinguished from {@link minLaneSeparation} only so the reason a
   * lane went quiet stays honest for the recovery surface; the behaviour is identical.
   */
  duplicateReadingSeparation: 0.05,

  /**
   * The furthest a body's centre may plausibly move between two frames and still be believed to be
   * the same body.
   *
   * This is what continuity matching is gated on. Generous — a five-year-old lunging sideways at
   * a 30 fps cadence is a real thing — but bounded, because an unbounded nearest-match will happily
   * follow a body across the whole picture into somebody else's lane.
   */
  maxFrameDisplacement: 0.12,

  /**
   * How far a body may cross the lane midline before position alone reassigns it.
   *
   * Position is only consulted when there is no continuity to match against, and even then a body
   * leaning over the line is not a body that changed lanes.
   */
  midlineHysteresis: 0.05,

  /**
   * How long a **cannot-read** episode identity memory survives (invented, flagged; S4-4).
   *
   * The bound `pose.observationGapMs` cannot supply, and for the reason that makes the pair of them
   * a pair: frames keep arriving throughout an ambiguity episode, at the ordinary cadence, so the
   * frame-stream gap rule never fires. `DUPLICATE_READING`, `OVERLAP` and `COLLAPSED` say only
   * that the picture cannot be read — deliberately *not* evidence that the memory routing a lone
   * child is wrong (D-088(2)), because a child read twice for a frame must not lose the lane they
   * are standing in. Held for ever, though, that same tolerance lets a long unreadable stretch
   * conceal a whole change of body while the departed child's lane is kept warm for whoever comes
   * out of it.
   *
   * A second **measured** floor, from the same spike reading {@link
   * COOPERATIVE_RECOVERY_THRESHOLDS.ambiguityNoticeMs} is built on: a full occlusion collapsed two
   * bodies into one blended reading for ~17 frames (~570 ms), and that is two children walking past
   * each other rather than a problem. One second sits comfortably beyond it — so an ordinary
   * crossing keeps every lane exactly where it was, and D-082(4)'s "nothing flashes for a crossing"
   * rule is untouched — and just beyond `ambiguityNoticeMs`, which means identity memory is only
   * ever dropped for an episode a family is *already* being told about. What expires is memory, not
   * attribution: the reacquisition afterwards is neutral and positional, which costs a child who
   * never moved one frame in the lane they are already standing in.
   */
  ambiguityMemoryMs: 1_000,

  /**
   * How many consecutive **empty** frames the midline memory survives (invented, flagged; S2).
   *
   * {@link midlineHysteresis} keeps a lone child near the middle in their own lane across the
   * frames the detector blinks in. What it must not do is survive the room emptying: after a full
   * loss the first body to walk back in is not necessarily the child who left, and inheriting the
   * departed child's lane puts the sibling's every answer on their evidence record with no signal
   * that anything happened (the D-069 family — full loss ⇒ neutral re-acquisition — read at this
   * layer).
   *
   * Two, because a dropped detection is a frame or two and a room somebody left is many; and
   * because the failure directions are not symmetric. Too short costs a lone child near the midline
   * one re-acquisition by position, which is where they are standing anyway. Too long attributes
   * one child's movements to their sibling. It fails safe towards deciding again from what the
   * camera can currently see.
   */
  midlineMemoryFrames: 2,

  /**
   * How many frames of "only one body" are a normal opening rather than a missing child.
   *
   * D-077(2)(d) measured second-body acquisition at **5–7 frames** (~170–230 ms at 30 fps), even
   * with both bodies stationary, far apart and unoccluded from the first frame. Seven, the top of
   * the measured range, because the cost of waiting is a fifth of a second and the cost of not
   * waiting is telling a family a child is missing every time a cooperative sitting starts.
   *
   * It applies **only to a lane that has never been acquired**. Once a lane is live, absence is
   * passed straight through and the shipped `trackingLossPauseMs` ladder owns the timing, exactly as
   * it does for a single child — a cooperative sitting must not be slower to notice a child than a
   * solo one.
   */
  acquisitionFrames: 7,

  /**
   * How far a reading's measured shoulder-to-hip scale may differ from a lane's calibrated
   * `bodyScale`, as a fraction, before it is not that child at all.
   *
   * A **veto, never a decision**: it can only withhold an attribution. D-077(2)(c) is why it exists
   * — the blended reading a full occlusion collapses into had a span sitting between both bodies and
   * still passed the 0.6 confidence gate, so confidence alone cannot catch identity corruption and
   * geometry has to.
   */
  maxScaleDeviation: 0.4,

  /**
   * How different two children's calibrated body scales must be before scale is allowed to say
   * anything about which of them is which.
   *
   * The honest floor on a tiebreaker: two similarly-sized siblings — the common case — give scale no
   * power whatsoever, and a mechanism that used it anyway would be inventing a distinction from
   * noise. Above this ratio the two calibrations genuinely separate, and scale may break a tie or
   * report a suspected swap; below it, the lane is the identity (MIN §6) and that bound is recorded
   * rather than hidden.
   */
  scaleDistinguishRatio: 1.25,

  /**
   * How much one child's **own** apparent scale moves about, as a fraction (invented, flagged;
   * S3-4/S3-5).
   *
   * The margin every scale comparison is bounded by, and there is exactly one of them on purpose:
   * the round-2 code grew a separate ad-hoc margin per comparison site, and each one was a fresh
   * guess at the same physical quantity. That quantity is not "how different are these two children"
   * — it is **how different is one child from themselves**, from one frame to the next, and the
   * answer is: quite. A shoulder-to-hip span is a point sample of a moving five-year-old. They
   * crouch to look at the floor, lean towards the lens, stand up on their toes, turn side-on. Every
   * one of those moves the reading by more than the gap between two siblings' profiles at the floor
   * of {@link scaleDistinguishRatio}.
   *
   * So a cross-profile advantage smaller than this is **noise, not evidence**, whatever it is being
   * asked about. Fifteen hundredths, bounded from both sides by executed cases rather than taste:
   * the reviewer's crouching child produces an advantage of ~0.11 and must not be refused, and a
   * child genuinely standing in their sibling's lane produces ~0.20 even when the two profiles sit
   * exactly at the distinguishability floor, and must be. It fails safe towards attributing — a
   * false negative is the recorded lane-is-identity bound (D-079(2)); a false positive takes a
   * child's answers away and tells a family to fix something that never happened.
   */
  scaleVariationAllowance: 0.15,

  /**
   * How many consecutive frames scale evidence must hold before it may overrule a child's own lane
   * (invented, flagged; S3-4).
   *
   * Scale evidence acts **immediately** where there is nothing standing against it — a body that
   * appeared in a lane it was not matched into, or is standing in the other child's half. This
   * covers the one case where something *is*: a body matched frame to frame, in its own half, whose
   * measurements have started disagreeing with its own profile. That is either a posture excursion
   * or a sibling who really has taken over the lane, and one frame cannot tell them apart.
   *
   * Three, ~100 ms at the target cadence and ~200 ms at the slow one: longer than a single
   * mis-measured frame, and far inside the 900 ms a family waits before the recovery surface says
   * anything at all, so a real mismatch still reaches them at the same moment it always did. The
   * cost of the wait is bounded and known — at most three frames of a sibling's movement can reach a
   * child's record in the one arrangement where continuity and the lane both said it was theirs.
   */
  scaleCorroborationFrames: 3,
};

/**
 * The shape, with **numbers rather than the literals above**.
 *
 * Derived with `as const` until round 7, which made every field its own literal type and the whole
 * object impossible to override — a tunables record that cannot be tuned, and a type that would have
 * blocked the physical session's first retune as surely as it blocked a test. `GameplayTunables`
 * beside it has always been declared this way; this now matches.
 */
export type CooperativeLaneThresholds = {
  -readonly [Key in keyof typeof COOPERATIVE_LANE_THRESHOLDS]: number;
};

/**
 * The one number the cooperative *recovery* surface needs (Slice E′, D-076(2)).
 *
 * Kept out of {@link COOPERATIVE_LANE_THRESHOLDS} deliberately: everything above decides what the
 * resolver may claim, and this decides only how long a family waits before a screen says anything
 * about it. The resolver would behave identically if this did not exist.
 */
export const COOPERATIVE_RECOVERY_THRESHOLDS = {
  /**
   * How long a lane must stay unreadable before a family is told anything (invented, flagged).
   *
   * The floor is measured rather than chosen: the Slice 0′ spike watched a full occlusion collapse
   * two bodies into one blended reading for **~17 frames (~570 ms)** (D-077(2)(c)), which is an
   * ordinary crossing rather than a problem. Anything shorter than that would put an invitation on
   * screen every time two children walked past each other — the flicker the brief forbids. This
   * sits comfortably beyond it, so what trips it is a situation that is not resolving itself: two
   * children standing on top of each other, or a sustained swap.
   *
   * It fails safe towards silence: too long means the game says nothing while nothing is being
   * attributed, and nothing is ever scored against a child for it (unanswered is never wrong).
   * Physical-session candidate like every cooperative number — none of them has met a child.
   */
  ambiguityNoticeMs: 900,
} as const;

export type CooperativeRecoveryThresholds = typeof COOPERATIVE_RECOVERY_THRESHOLDS;
